import React from "react"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import ModalPortal from "../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import { ButtonIcon } from "../Button/Button"
import { faClose, faLink } from "@fortawesome/free-solid-svg-icons"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { PopupHelpButton } from "../Help/PopupHelpButton"
import GetSpace from "../../utils/space"
import { useTranslation } from "react-i18next"

export interface AssociationItem {
  id: number
  title: string
  subtitle?: string
  badge?: string
}

interface AssociationsPopupProps {
  title: string
  emptyMessage: string
  onClose: () => void
  items: AssociationItem[]
}

export const AssociationsPopup: React.FC<AssociationsPopupProps> = ({ title, emptyMessage, onClose, items }) => {
  const { t } = useTranslation("helpPopup")

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.fadeInTraslate} duration="0.3s">
          <section className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faLink}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.shared.associations.1"
                    title={t("global.association.title")}
                    description={t("global.association.description")}
                    bullets={t("global.association.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:components.associations.associationsPopup.button.onClose"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
              <div className="p-2">
                {items.length === 0 && (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-600">
                    {emptyMessage}
                  </div>
                )}

                {items.map((item, index) => (
                  <Animate
                    key={`${item.id}-${item.title}`}
                    type={AnimationTypes.fadeInTraslate}
                    duration={`${1.4 + index * 0.6}s`}
                  >
                    <div className="mb-2 rounded-lg border border-orange-100 bg-gradient-to-br from-white via-orange-50 to-orange-100/40 p-3 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-slate-800">{item.title}</p>
                          {item.subtitle && <p className="mt-1 text-xs text-slate-600">{item.subtitle}</p>}
                        </div>

                        {item.badge && (
                          <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-[11px] font-bold text-orange-700">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </div>
                  </Animate>
                ))}
              </div>
            </Animate>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
